import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    console.log('selectedVariantId', selectedVariantId)
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID scoped to the clicked product card.
   * Never reads from the global quick-add modal to avoid stale cross-product state.
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    if (!productCard) return null;

    // Prefer product card's own helper (checked radio with data-variant-id)
    const viaCard = productCard.getSelectedVariantId?.();
    if (viaCard) return viaCard;

    // Fallback: select-based pickers on the card
    const select = productCard.querySelector('select.variant-option__select');
    const opt = select?.selectedOptions?.[0];
    if (opt instanceof HTMLOptionElement && opt.dataset.variantId) return opt.dataset.variantId;

    return null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    // Add loading state to the button
    const button = event.target instanceof HTMLElement ? event.target.closest('button') : null;
    if (button) {
      button.classList.add('loading');
    }

    try {
      const currentUrl = this.productPageUrl;

      // Check if we have cached content for this URL
      let productGrid = this.#cachedContent.get(currentUrl);

      if (!productGrid) {
        // Fetch and cache the content
        const html = await this.fetchProductPage(currentUrl);
        if (html) {
          const gridElement = html.querySelector('[data-product-grid-content]');
          if (gridElement) {
            // Cache the cloned element to avoid modifying the original
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, productGrid);
          }
        }
      }

      if (productGrid) {
        // Use a fresh clone from the cache
        const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
        await this.updateQuickAddModal(freshContent);
      }

      this.#openQuickAddModal();
    } catch (error) {
      console.error('Error in quick add:', error);
    } finally {
      // Remove loading state from the button
      if (button) {
        button.classList.remove('loading');
      }
    }
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      console.log('productDetails', productDetails)
      if (!productDetails) return;
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productSizeGuide = productGrid.querySelector('.product-size-guide');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      if (!productFormComponent || !variantPicker || !productPrice) {
  console.error('Quick add: required product content is missing', {
    productFormComponent,
    variantPicker,
    productPrice,
  });
  return;
}

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);
      if (productSizeGuide) {
  productGrid.appendChild(productSizeGuide);
}

      // Add "View Full Details" link for mobile
      const viewFullDetailsLink = document.createElement('a');
      viewFullDetailsLink.href = this.productPageUrl;
      viewFullDetailsLink.textContent = 'View Full Details';
      viewFullDetailsLink.classList.add('view-full-details-link');
      productGrid.appendChild(viewFullDetailsLink);
      
      productDetails.remove();
    } else {
      // Add "View Full Details" link for desktop
      const productFormComponent = productGrid.querySelector('product-form-component');
      if (productFormComponent) {
        const viewFullDetailsLink = document.createElement('a');
        viewFullDetailsLink.href = this.productPageUrl;
        viewFullDetailsLink.textContent = 'View Full Details';
        viewFullDetailsLink.classList.add('view-full-details-link');
        
        // Insert after the product form component
        productFormComponent.parentNode?.insertBefore(viewFullDetailsLink, productFormComponent.nextSibling);
      }
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
    
    // Set up size guide functionality
    this.#setupSizeGuideHandler(modalContent);
    
    // Update the main size guide modal content when quick-add modal opens
    this.#updateMainSizeGuideModal(modalContent);
    
    // Initialize Swiper instances for the modal content
    this.#initializeVariantSwipers(modalContent);

    // Minimal size enforcement inside modal: ensure size picked before submit
    const form = modalContent.querySelector('product-form-component form');
    if (form instanceof HTMLFormElement && form.dataset.sizeEnforced !== 'true') {
      form.dataset.sizeEnforced = 'true';
      form.addEventListener('submit', (e) => {
        const sizeGroup = modalContent.querySelector('.variant-option[data-is-size-option="true"]');
        if (!sizeGroup) return;

        const checkedRadio = sizeGroup.querySelector('input[type="radio"]:checked');
        const selectEl = /** @type {HTMLSelectElement | null} */ (
          sizeGroup.querySelector('select.variant-option__select') || null
        );
        const selectHasValue = selectEl ? selectEl.value !== '' : false;

        if (!checkedRadio && !selectHasValue) {
          e.preventDefault();
          const error = modalContent.querySelector('[ref="addToCartTextError"]');
          const message = Theme?.translations?.select_size_prompt || Theme?.translations?.select_size || 'Please select a size';
          if (error instanceof HTMLElement) {
            error.classList.remove('hidden');
            const span = error.querySelector('.product-form-text__error-message');
            if (span) span.textContent = message;
            setTimeout(() => error.classList.add('hidden'), 5000);
          }
          const first = sizeGroup.querySelector('input[type="radio"]');
          if (first instanceof HTMLElement) {
            first.focus();
          } else if (selectEl instanceof HTMLElement) {
            selectEl.focus();
          }
        }
      });
    }
  }

  /**
   * Updates the main size guide modal content with content from quick-add modal
   * @param {Element} modalContent - The quick-add modal content element
   */
  #updateMainSizeGuideModal(modalContent) {
    // Find the size guide content within the quick-add modal content
    const sizeGuideContent = modalContent.querySelector('.size-guide-modal__content');
    if (!sizeGuideContent) return;

    // Find the main size guide modal in the document
    const mainSizeGuideModal = document.querySelector('.quick-add-size .size-guide-modal__content');
    if (mainSizeGuideModal) {
      console.log('Updating main size guide modal with content from quick-add modal');
      
      // Create a new div after the main size guide modal
      let newContentDiv = mainSizeGuideModal.parentElement?.querySelector('.quick-add-size-guide-content');
      
      if (!newContentDiv) {
        // Create new div if it doesn't exist
        newContentDiv = document.createElement('div');
        newContentDiv.classList.add('quick-add-size-guide-content');
        newContentDiv.classList.add('gen:px-6', 'gen:pb-4');
        
        // Insert after the main size guide modal content
        mainSizeGuideModal.parentElement?.appendChild(newContentDiv);
      }
      
      // Replace the content in the new div
      newContentDiv.innerHTML = sizeGuideContent.innerHTML;
      
      // Hide the original content and show the new content
      if (mainSizeGuideModal instanceof HTMLElement) {
        mainSizeGuideModal.style.display = 'none';
      }
      if (newContentDiv instanceof HTMLElement) {
        newContentDiv.style.display = 'block !important';
      }
      
      console.log('Main size guide modal content updated in new div');
    }
  }

  /**
   * Sets up size guide functionality for the modal content
   * @param {Element} modalContent - The modal content element
   */
  #setupSizeGuideHandler(modalContent) {
    // Find the size guide button in the modal content
    const sizeGuideButton = modalContent.querySelector('#size-guide-modal-button');
    if (!sizeGuideButton) return;

    // Add click handler to the size guide button to just open the modal
    sizeGuideButton.addEventListener('click', (event) => {
      event.preventDefault();
      
      console.log('Size guide button clicked');
      const sizeGuideModal = document.querySelector('.quick-add-size #size-guide-modal');
      const modalinside = document.querySelector('.quick-add-size .modal-content-inside');
      
      if (sizeGuideModal instanceof HTMLElement) {
        sizeGuideModal.style.display = 'block';
        document.body.style.overflow = 'hidden';
        sizeGuideModal.classList.add('qu')
        
        // Set background color only if modalinside exists
        if (modalinside instanceof HTMLElement) {
          modalinside.style.backgroundColor = 'white';
          modalinside.style.maxWidth = '70%';
          modalinside.style.marginInline = 'auto';
          modalinside.style.height = '100%';
        }
        
        // Focus management for accessibility
        const closeButton = sizeGuideModal.querySelector('button[aria-label="Close sizing guide"]');
        if (closeButton instanceof HTMLElement) {
          closeButton.focus();
          closeButton.addEventListener('click', () => {
            sizeGuideModal.style.display = 'none';
            document.body.style.overflow = '';
            if (modalinside instanceof HTMLElement) {
              modalinside.style.backgroundColor = '';
              modalinside.style.maxWidth = '';
              modalinside.style.marginInline = '';
              modalinside.style.height = '';
            }
          });
        }
      }
    });
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    console.log('modalInputs', modalInputs, 'selectedVariantId', selectedVariantId)
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));

        // Immediately sync the hidden variant ID field to prevent stale submissions.
        // In some edge cases, the VariantUpdateEvent can arrive after a fast Add to Cart click,
        // leading to the previous variant being submitted. Setting it here removes that race.
        const variantIdInput = modalContent.querySelector('product-form-component input[name="id"]');
        if (variantIdInput instanceof HTMLInputElement) {
          variantIdInput.value = selectedVariantId;
        }
        break;
      }
    }
  }

  /**
   * Initialize Swiper instances for variant pickers in the modal content
   * @param {Element} container - The container element to search for Swiper elements
   */
  #initializeVariantSwipers(container) {
    if (typeof /** @type {any} */ (window).Swiper === 'undefined') {
      console.warn('Swiper is not loaded. Make sure swiper-bundle.min.js is included.');
      return;
    }

    const swiperElements = container.querySelectorAll('.variant-swiper');
    swiperElements.forEach((swiperElement) => {
      // Skip if element is not properly connected to DOM
      if (!(swiperElement instanceof Element) || !swiperElement.isConnected) {
        return;
      }
      
      try {
        // Destroy existing instance if it exists
        if (/** @type {any} */ (swiperElement).swiper && typeof /** @type {any} */ (swiperElement).swiper.destroy === 'function') {
          /** @type {any} */ (swiperElement).swiper.destroy(true, true);
        }
        
        // Get the option type from the class (e.g., variant-swiper-color, variant-swiper-size)
        const optionClass = Array.from(swiperElement.classList).find(cls => cls.startsWith('variant-swiper-'));
        const optionType = optionClass ? optionClass.replace('variant-swiper-', '') : '';
        
        // Create new instance with unique navigation buttons
        const swiper = new /** @type {any} */ (/** @type {any} */ (window).Swiper)(swiperElement, {
          slidesPerView: 'auto',
          spaceBetween: 10,
          freeMode: true,
          grabCursor: true,
          slideToClickedSlide: true,
          centeredSlides: false,
          navigation: {
            nextEl: `.swiper-button-next-${optionType}`,
            prevEl: `.swiper-button-prev-${optionType}`,
          },
          keyboard: {
            enabled: true,
            onlyInViewport: true,
          },
          on: {
            init: function() {
              // Slide to active variant on initialization
              setTimeout(() => {
                /** @type {any} */ (this).slideToActiveVariant();
              }, 100);
            },
          },
        });

        // Add method to slide to active variant
        swiper.slideToActiveVariant = function() {
          const activeSlide = this.el.querySelector('.swiper-slide input:checked');
          if (activeSlide) {
            const slideIndex = Array.from(this.slides).findIndex(slide => 
              slide.contains(activeSlide)
            );
            if (slideIndex !== -1) {
              this.slideTo(slideIndex, 300);
            }
          }
        };
      } catch (e) {
        console.warn('Failed to initialize Swiper for variant picker:', e);
      }
    });
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateViewFullDetailsLink);
    this.addEventListener(ThemeEvents.variantUpdate, this.#reinitializeSwipers);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  /**
   * Updates the "View Full Details" link when variant changes
   * @param {CustomEvent} event - The variant update event
   */
  #updateViewFullDetailsLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewFullDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-full-details-link'));

    if (!anchorElement || !viewFullDetailsLink) return;

    viewFullDetailsLink.href = anchorElement.href;
  };

  #reinitializeSwipers = () => {
    // Small delay to ensure DOM has been updated
    setTimeout(() => {
      const modalContent = document.getElementById('quick-add-modal-content');
      if (modalContent) {
        this.#initializeVariantSwipers(modalContent);
      }
    }, 100);
  };

  /**
   * Initialize Swiper instances for variant pickers in the modal content
   * @param {Element} container - The container element to search for Swiper elements
   */
  #initializeVariantSwipers(container) {
    if (typeof /** @type {any} */ (window).Swiper === 'undefined') {
      console.warn('Swiper is not loaded. Make sure swiper-bundle.min.js is included.');
      return;
    }

    const swiperElements = container.querySelectorAll('.variant-swiper');
    swiperElements.forEach((swiperElement) => {
      // Skip if element is not properly connected to DOM
      if (!(swiperElement instanceof Element) || !swiperElement.isConnected) {
        return;
      }
      
      try {
        // Destroy existing instance if it exists
        if (/** @type {any} */ (swiperElement).swiper && typeof /** @type {any} */ (swiperElement).swiper.destroy === 'function') {
          /** @type {any} */ (swiperElement).swiper.destroy(true, true);
        }
        
        // Get the option type from the class (e.g., variant-swiper-color, variant-swiper-size)
        const optionClass = Array.from(swiperElement.classList).find(cls => cls.startsWith('variant-swiper-'));
        const optionType = optionClass ? optionClass.replace('variant-swiper-', '') : '';
        
        // Create new instance with unique navigation buttons
        const swiper = new /** @type {any} */ (/** @type {any} */ (window).Swiper)(swiperElement, {
          slidesPerView: 'auto',
          spaceBetween: 10,
          freeMode: true,
          grabCursor: true,
          slideToClickedSlide: true,
          centeredSlides: false,
          navigation: {
            nextEl: `.swiper-button-next-${optionType}`,
            prevEl: `.swiper-button-prev-${optionType}`,
          },
          keyboard: {
            enabled: true,
            onlyInViewport: true,
          },
          on: {
            init: function() {
              // Slide to active variant on initialization
              setTimeout(() => {
                /** @type {any} */ (this).slideToActiveVariant();
              }, 100);
            },
          },
        });

        // Add method to slide to active variant
        swiper.slideToActiveVariant = function() {
          const activeSlide = this.el.querySelector('.swiper-slide input:checked');
          if (activeSlide) {
            const slideIndex = Array.from(this.slides).findIndex(slide => 
              slide.contains(activeSlide)
            );
            if (slideIndex !== -1) {
              this.slideTo(slideIndex, 300);
            }
          }
        };
      } catch (e) {
        console.warn('Failed to initialize Swiper for variant picker:', e);
      }
    });
  }

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
